import {
  SCAN_TYPE_LABELS,
  SCAN_TYPE_DESCRIPTIONS,
  SCAN_TYPE_DURATIONS,
  type ScanType,
} from '@nirvik/shared';
import { cn } from '@/lib/utils';
import { Clock, Shield, Globe, Wifi, Bug, Zap } from 'lucide-react';

const scanTypeIcons: Record<ScanType, React.ElementType> = {
  full: Shield,
  ssl: Globe,
  headers: Shield,
  ports: Wifi,
  dast: Bug,
  'api-fuzz': Zap,
};

const scanTypes: ScanType[] = ['full', 'ssl', 'headers', 'ports', 'dast', 'api-fuzz'];

interface ScanTypeSelectorProps {
  value: ScanType;
  onChange: (type: ScanType) => void;
}

export function ScanTypeSelector({ value, onChange }: ScanTypeSelectorProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {scanTypes.map((type) => {
        const Icon = scanTypeIcons[type];
        const selected = value === type;

        return (
          <button
            key={type}
            type="button"
            onClick={() => onChange(type)}
            className={cn(
              'relative flex flex-col items-start gap-3 rounded-xl border-2 p-4 text-left transition-all',
              selected
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500'
                : 'border-gray-200 dark:border-border bg-white dark:bg-card hover:border-gray-300 dark:hover:border-border hover:bg-gray-50 dark:hover:bg-muted'
            )}
          >
            {/* Radio indicator */}
            <div className="absolute top-4 right-4">
              <div
                className={cn(
                  'h-5 w-5 rounded-full border-2 flex items-center justify-center',
                  selected ? 'border-blue-500' : 'border-gray-300'
                )}
              >
                {selected && <div className="h-2.5 w-2.5 rounded-full bg-blue-500" />}
              </div>
            </div>

            <div
              className={cn(
                'h-10 w-10 rounded-lg flex items-center justify-center',
                selected ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-gray-100 dark:bg-muted'
              )}
            >
              <Icon className={cn('h-5 w-5', selected ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground dark:text-muted-foreground')} />
            </div>

            <div className="pr-6">
              <h3 className={cn('text-sm font-semibold', selected ? 'text-blue-900 dark:text-blue-300' : 'text-gray-900 dark:text-foreground')}>
                {SCAN_TYPE_LABELS[type]}
              </h3>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1 line-clamp-2">
                {SCAN_TYPE_DESCRIPTIONS[type]}
              </p>
            </div>

            <div className="flex items-center gap-1 text-xs text-muted-foreground dark:text-muted-foreground">
              <Clock className="h-3 w-3" />
              {SCAN_TYPE_DURATIONS[type]}
            </div>
          </button>
        );
      })}
    </div>
  );
}
